import styled from '@emotion/styled';
import {Color, useThemeColors} from '@nfq/colors';
import {spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {PayloadLexicalSmall} from 'UI/components/layout/PayloadLexicalSmall';
import {H6, P} from 'UI/components/layout/Text';

import {FadeInAnimation} from 'UI/animations/shared';

import type {LexicalContent} from '@nfq/payload-lexical-parser';

/**
 * The props for the `Card` component, which renders a card with an icon,
 * optional headline, and descriptive text.
 * This interface defines the structure of the card’s content, including
 * its headline, icon image, and text. It also supports a `testId` for
 * end-to-end testing purposes.
 */
interface ComponentProps {
    /**
     * Optional headline text displayed below the icon image.
     * Used to provide a title or label for the card.
     */
    headline?: string | null;
    /**
     * The optional index number displayed at the top of the card.
     * Typically used to indicate the step number in a sequence.
     */
    index?: number;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Card'
     */
    testId?: string;
    /**
     * Optional descriptive text content for the card, rendered as
     * rich text using the `PayloadLexical` component.
     */
    text?: LexicalContent | null;
}

/**
 * A card component that displays an icon, a headline, and optional descriptive text.
 * This component is designed for use in grid or feature sections, where each card
 * highlights a specific item or concept. It renders an image at the top, followed by
 * an optional headline and rich text description. The rich text is rendered using
 * the `PayloadLexical` component for flexible content formatting.
 * The `testId` prop is applied as a `data-cy` attribute to support reliable end-to-end testing.
 * The component is wrapped in MobX's `observer`, allowing it to automatically re-render
 * in response to observable state changes.
 *
 * @param props          The component props.
 * @param props.headline Optional headline text displayed as a label under the icon.
 * @param props.index    The index number displayed at the top of the card, typically indicating the step number.
 * @param props.text     Optional descriptive text, rendered as rich text content.
 * @param props.testId   An optional identifier used for testing, applied as `data-cy`. Defaults to `'Card'`.
 * @returns A JSX element rendering the card with an icon, headline, and text.
 *
 * @example
 * ```tsx
 * <Card
 *   headline="Fast Performance"
 *   index={index}
 *   text="Our platform is optimized for speed and reliability."
 * />
 * ```
 */
const Card = observer(({headline, index, testId = 'Card', text}: ComponentProps) => {
    const colors = useThemeColors();

    return (
        <Wrapper data-cy={testId} variants={FadeInAnimation}>
            <Step $size="small" variants={FadeInAnimation}>
                <Color $color={colors.textPrimary}>Schritt {(index ?? 0) + 1}</Color>
            </Step>
            <TextWrapper>
                {headline && <MotionH6 as="h4" variants={FadeInAnimation}>{headline}</MotionH6>}
                {text && (
                    <motion.div variants={FadeInAnimation}>
                        <PayloadLexicalSmall description={text} />
                    </motion.div>
                )}
            </TextWrapper>
        </Wrapper>
    );
});

Card.displayName = 'Card';

export {Card};

const Wrapper = styled(motion.div)`
    border: 1px solid ${({theme}) => theme.colors.neutral200};
    border-radius: 24px;
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
    padding: ${spacing(8)};
    width: 100%;
`;

const Step = styled(motion.create(P))`
    background-color: ${({theme}) => theme.colors.neutral100};
    border-radius: 8px;
    padding: ${spacing(2)} ${spacing(5)};
    width: fit-content;
`;

const TextWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(8)};
    padding-inline-start: ${spacing(2)};
    width: 100%;
`;

const MotionH6 = styled(motion.create(H6))``;